import { useEffect, useState } from "react";
import api from "@/lib/api";
import { mergePublicSiteForEditor, resolvePublicSiteContent } from "@/lib/gymSiteDefaults";

/** Título de pestaña y favicon según configuración del gimnasio */
export function applyBranding(settings = {}) {
  const gymName = (settings.gym_name || "").trim() || "Gimnasio";
  const logo = settings.logo || "";
  const fallbackIcon = `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  document.title = gymName;

  const desc = document.querySelector('meta[name="description"]');
  if (desc) desc.setAttribute("content", gymName);

  document.querySelectorAll('link[data-branding="true"]').forEach((el) => el.remove());

  const href = logo || fallbackIcon;
  const typeMatch = typeof logo === "string" && logo.match(/^data:(image\/[^;]+)/);
  const type = typeMatch ? typeMatch[1] : undefined;

  ["icon", "shortcut icon", "apple-touch-icon"].forEach((rel) => {
    const link = document.createElement("link");
    link.setAttribute("rel", rel);
    link.setAttribute("href", href);
    link.setAttribute("data-branding", "true");
    if (type) link.setAttribute("type", type);
    document.head.appendChild(link);
  });
}

export const DEFAULT_GYM_TIMEZONE = "America/Mexico_City";

/** Zona del gimnasio: México centro por defecto si no hay otra configurada. */
export function resolveGymTimezone(settings = {}) {
  const tz = (settings?.timezone || "").trim();
  if (!tz || tz === "UTC") return DEFAULT_GYM_TIMEZONE;
  const compact = tz.toLowerCase().replace(/\s+/g, "");
  if (compact.includes("utc-6") || compact.includes("gmt-6") || compact === "cst") {
    return DEFAULT_GYM_TIMEZONE;
  }
  if (/mexico|cancun/i.test(tz)) return DEFAULT_GYM_TIMEZONE;
  try {
    Intl.DateTimeFormat("es-MX", { timeZone: tz });
    return tz;
  } catch {
    return DEFAULT_GYM_TIMEZONE;
  }
}

export function formatInGymTimezone(date, timezone, options = {}) {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat("es-MX", { timeZone: timezone, ...options }).format(d);
}

/** Fecha YYYY-MM-DD del calendario del gimnasio (no UTC del navegador). */
export function isAttendanceAvailable(settings = {}) {
  if (settings.attendance_available !== undefined) {
    return settings.attendance_available !== false;
  }
  const licensed = settings.attendance_licensed ?? true;
  const enabled = settings.attendance_enabled ?? true;
  return Boolean(licensed && enabled);
}

export function gymTodayIsoDate(timezone = DEFAULT_GYM_TIMEZONE) {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function useSettings() {
  const [settings, setSettings] = useState({ gym_name: "BOXING GYM" });
  useEffect(() => {
    const load = () =>
      api
        .get("/settings")
        .then((r) => {
          const data = r.data || {};
          setSettings(data);
          applyBranding(data);
        })
        .catch(() => {});
    load();
    const onUpdated = (e) => {
      if (e?.detail) {
        setSettings(e.detail);
        applyBranding(e.detail);
      } else load();
    };
    window.addEventListener("app-settings-updated", onUpdated);
    return () => window.removeEventListener("app-settings-updated", onUpdated);
  }, []);
  return settings;
}

export function usePublicSite() {
  const [site, setSite] = useState(() => resolvePublicSiteContent(null));
  useEffect(() => {
    const load = () =>
      api
        .get("/public-site")
        .then((r) => {
          const merged = mergePublicSiteForEditor(r.data);
          setSite(merged);
          applyBranding({ gym_name: merged.gym_name, logo: merged.logo });
        })
        .catch(() => {});
    load();
    const onUpdated = (e) => {
      if (e?.detail) {
        const merged = mergePublicSiteForEditor(e.detail);
        setSite(merged);
        applyBranding({ gym_name: merged.gym_name, logo: merged.logo });
      } else load();
    };
    window.addEventListener("public-site-updated", onUpdated);
    return () => window.removeEventListener("public-site-updated", onUpdated);
  }, []);
  return site;
}

export const fmtCurrency = (amount, symbol = "$") => {
  const n = Number(amount || 0);
  return `${symbol}${n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

export function getOfferTimeLeft(isoDate, now = Date.now()) {
  if (!isoDate) return null;
  const ms = new Date(isoDate).getTime() - now;
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) {
    return { h: "00", m: "00", s: "00", expired: true };
  }
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return {
    h: String(h).padStart(2, "0"),
    m: String(m).padStart(2, "0"),
    s: String(s).padStart(2, "0"),
    expired: false,
  };
}

export function isPackageOfferLimited(pkg) {
  return !!pkg?.offer_expires_at;
}

export function isPackageOfferExpired(pkg, now = Date.now()) {
  if (!pkg?.offer_expires_at) return false;
  const left = getOfferTimeLeft(pkg.offer_expires_at, now);
  return !!left?.expired;
}

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export function packagePaymentAmounts(pkg, paymentType) {
  if (!pkg) return { inscription_part: 0, monthly_part: 0, total: 0 };
  const monthly = Number(pkg.monthly_cost || 0);
  const inscriptionCost = Number(pkg.inscription_cost || 0);
  const inscriptionFree = !!pkg.inscription_free;
  if (paymentType === "monthly") {
    return { inscription_part: 0, monthly_part: monthly, total: monthly };
  }
  if (inscriptionFree) {
    return { inscription_part: 0, monthly_part: monthly, total: monthly };
  }
  return {
    inscription_part: inscriptionCost,
    monthly_part: monthly,
    total: inscriptionCost + monthly,
  };
}
